import DateRangePicker from '@wojtekmaj/react-daterange-picker'
import dayjs from 'lib/dayjs'
import { some } from 'lodash'
import { useTranslation } from 'app/i18n/client'
import ArrowLeft from 'public/icons/arrow-left.svg'
import ArrowRight from 'public/icons/arrow-right.svg'
import { RefObject } from 'react'
import { toast } from 'react-toastify'
import { formatDate } from 'utils'
import { MAX_NIGHS_RESERVATION } from 'utils/constants'
import { setDateStyles } from '../utils'
import { useParams } from 'next/navigation'

interface DateRageSelectProps {
  calendarRef: RefObject<HTMLDivElement | null>
  currentActiveStartDate: Date
  selectedValue: (Date | null)[]
  today: Date
  disabledDates?: Date[]
  ebisuDisabledDates?: Date[]
  showDoubleView?: boolean
  onSelectValue?: (value: (Date | null)[]) => void
  handleCancel: () => void
  setCurrentActiveStartDate: (value: Date) => void
  onClose?: () => void
  onOpenMaxNightModal: () => void
}

const DateRangeSelect: React.FC<DateRageSelectProps> = ({
  calendarRef,
  showDoubleView,
  currentActiveStartDate,
  selectedValue,
  today,
  disabledDates,
  ebisuDisabledDates,
  onSelectValue,
  handleCancel,
  setCurrentActiveStartDate,
  onClose,
  onOpenMaxNightModal,
}) => {
  const { t } = useTranslation()
  const params = useParams<{ locale: string }>()
  const { locale } = params || {}
  const [fromValue, toValue] = selectedValue || []

  // Combine API disabled dates + Ebisu disabled dates for total disabled dates
  const allDisabledDates = [
    ...(disabledDates || []),
    ...(ebisuDisabledDates || []),
  ]

  const firstDisabledDateAfterFromDate =
    !!fromValue && !toValue
      ? allDisabledDates?.filter((date) =>
          dayjs(date).isAfter(dayjs(fromValue)),
        )?.[0]
      : undefined

  const disabledValues = allDisabledDates?.filter(
    (date) => date !== firstDisabledDateAfterFromDate,
  )

  return (
    // Scope class for the dated search calendar only: the room-plan calendar
    // reuses the same `.react-daterange-picker` CSS, so its price tiles and
    // taller cells must stay untouched (app/styles/calendar.css).
    <div className="calendar--dated">
      <DateRangePicker
        value={selectedValue as [Date | null, Date | null]}
        locale={locale}
        rangeDivider=""
        minDate={today}
        isOpen={true}
        calendarProps={{
          calendarType: 'gregory',
          inputRef: calendarRef as unknown as React.Ref<HTMLDivElement>,
          showDoubleView: showDoubleView,
          activeStartDate: currentActiveStartDate,
          nextLabel: <ArrowRight className="fill-current text-gray" />,
          prevLabel: <ArrowLeft className="fill-current text-gray" />,
          formatDay: (_: string | undefined, date: Date) =>
            dayjs(date).get('date')?.toString(),
          onClickDay: (value: any) => {
            if (!onSelectValue) return

            const [startDate, endDate] = selectedValue

            if (startDate !== null && endDate === null) return

            onSelectValue([new Date(value.toDateString()), null])
          },
          onActiveStartDateChange: ({ action, activeStartDate }: any) => {
            if (action !== 'onChange') {
              setCurrentActiveStartDate(activeStartDate)
            }

            setTimeout(() => {
              setDateStyles(calendarRef, today)
            }, 0)
          },
          tileDisabled: ({ date }: any) =>
            !disabledValues
              ? false
              : disabledValues
                  .map((disabledDate) => formatDate(disabledDate))
                  .includes(formatDate(date)),
          tileClassName: ({ date }: any) => {
            const formattedDate = formatDate(date)
            const isEbisuDisabled =
              ebisuDisabledDates &&
              ebisuDisabledDates
                .map((disabledDate) => formatDate(disabledDate))
                .includes(formattedDate)

            const isApiDisabled =
              disabledDates &&
              disabledDates
                .map((disabledDate) => formatDate(disabledDate))
                .includes(formattedDate)

            // Ebisu cutoff + API no inventory → gray + strikethrough
            if (isEbisuDisabled && isApiDisabled) {
              return 'ebisu-no-inventory'
            }

            // Ebisu cutoff only → gray only (no class, uses default :disabled styling)
            if (isEbisuDisabled) {
              return null
            }

            // Non-Ebisu future sold-out date → #A7A7A7 text, no strikethrough
            return dayjs(date).isSameOrAfter(today) && isApiDisabled
              ? 'sold-out-future'
              : null
          },
          tileContent: ({ date, view }: any) => {
            if (view !== 'month') return null

            const isApiDisabled =
              disabledDates &&
              disabledDates
                .map((disabledDate) => formatDate(disabledDate))
                .includes(formatDate(date))

            // No-inventory dates are marked with a diagonal rule drawn in CSS
            // (.calendar--dated .sold-out-future), which carries no text — so the
            // waitlist wording stays here for screen readers only.
            if (isApiDisabled && dayjs(date).isSameOrAfter(today, 'day')) {
              return <span className="sr-only">{t('Sold out waitlist')}</span>
            }

            return null
          },
        }}
        onChange={(newValue: any) => {
          const [startDate, endDate] = newValue

          const totalNights = Math.abs(
            dayjs(endDate).diff(dayjs(startDate), 'days'),
          )

          if (totalNights > MAX_NIGHS_RESERVATION) {
            onOpenMaxNightModal()
            handleCancel()
            return
          }

          if (
            allDisabledDates.length > 0 &&
            some(allDisabledDates, (disabledDate) =>
              dayjs(disabledDate).isBetween(
                dayjs(startDate),
                dayjs(endDate),
                'day',
                '()',
              ),
            )
          ) {
            toast.warn(t('Fully booked in the period'))
            handleCancel()
            return
          }
          onSelectValue &&
            onSelectValue([
              new Date(startDate.toDateString()),
              new Date(endDate.toDateString()),
            ])

          onClose && onClose()
        }}
      />
    </div>
  )
}

export default DateRangeSelect
